import { useState, useEffect } from 'react';
import { Search, Loader2, BookOpen, ListChecks, BookMarked, Library, Clock, ChevronDown, Download, ChevronRight, Play, ChevronLeft, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { YouTubeService } from '@/lib/services/youtube';
import { VideoPlayer } from '@/components/video/video-player';
import { MCQData } from '@/lib/types/mcq';
import { GlossaryData } from '@/lib/types/glossary';
import { FlashcardSet } from '@/lib/types/flashcard';
import { FlashcardDisplay } from '@/components/flashcard/flashcard-display';
import { useTheme } from '@/components/theme/theme-provider';
import { TranscriptSegment } from '@/lib/types/transcript';
import { Chapter, BookData } from '@/lib/types/book';
import { PDFService } from '@/lib/services/pdf';
import { MindmapView } from '@/components/mindmap/mindmap-view';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/auth/context';
import { useLocation } from 'react-router-dom';

interface VideoDetails {
  id: string;
  title: string;
  thumbnail: string;
}

type TabType = 'notes' | 'mcq' | 'glossary' | 'flashcards' | 'mindmap';

interface GenerateOptions {
  generateNotes: boolean;
  generateMCQ: boolean;
  generateGlossary: boolean;
  generateFlashcards: boolean;
  generateMindmap: boolean;
}

interface GeneratedContent {
  notes: {
    summary: string;
    readingTime: string;
    keyTopics: string[];
    chapters: Array<{
      title: string;
      content: string;
      isActive: boolean;
      concepts: Array<{
        title: string;
        description: string;
      }>;
      takeaways: string[];
      quickCheck?: {
        question: string;
        options: string[];
        correctAnswer: number;
      };
    }>;
  } | null;
  mcq: MCQData | null;
  glossary: GlossaryData | null;
  flashcards: FlashcardSet | null;
  mindmap: string | null;
}

interface MCQQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export function NewOutput() {
  const { user } = useAuth();
  const location = useLocation();
  const [youtubeUrl, setYoutubeUrl] = useState(location.state?.youtubeUrl || '');
  const [isValidUrl, setIsValidUrl] = useState(!!location.state?.youtubeUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [videoDetails, setVideoDetails] = useState<VideoDetails | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('notes');
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [generateOptions, setGenerateOptions] = useState<GenerateOptions>({
    generateNotes: true,
    generateMCQ: true,
    generateGlossary: true,
    generateFlashcards: true,
    generateMindmap: true
  });
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent>({
    notes: null,
    mcq: null,
    glossary: null,
    flashcards: null,
    mindmap: null
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    if (location.state?.youtubeUrl) {
      setYoutubeUrl(location.state.youtubeUrl);
      setIsValidUrl(true);
    }
  }, [location.state]);

  const validateYoutubeUrl = (url: string) => {
    // Extract video ID from either format
    let videoId = '';
    
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
    } else if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0] || '';
    }

    const isValid = videoId.length === 11;
    setIsValidUrl(isValid);
    return isValid;
  };

  const handleGenerationError = (error: unknown) => {
    if (error instanceof Error) {
      if (error.message.includes('API key')) {
        toast.error('Service configuration error. Please try again later.');
      } else if (error.message.includes('Video not found')) {
        toast.error('Video not found. Please check the URL and try again.');
      } else if (error.message.includes('Invalid YouTube URL')) {
        toast.error('Invalid YouTube URL. Please check the URL and try again.');
      } else {
        toast.error('Failed to generate study materials. Please try again.');
      }
    } else {
      toast.error('An unexpected error occurred. Please try again.');
    }

    // Dismiss any loading toasts
    toast.dismiss('video-fetch');
    toast.dismiss('content-gen');
  };

  const handleSubmit = async () => {
    if (!validateYoutubeUrl(youtubeUrl)) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }

    if (!user) {
      toast.error('Please sign in to generate study materials');
      return;
    }

    // Reset all states to clear previous data
    setGeneratedContent({
      notes: null,
      mcq: null,
      glossary: null,
      flashcards: null,
      mindmap: null
    });
    setSelectedAnswers([]);
    setShowContent(false);
    setActiveTab('notes');
    setVideoDetails(null);

    setIsLoading(true);
    try {
      const videoId = YouTubeService.extractVideoId(youtubeUrl);
      const details = await YouTubeService.getVideoDetails(videoId);
      const categoryName = await YouTubeService.getCategoryName(details.categoryId);
      
      // Record conversion attempt in Supabase
      const { error: conversionError } = await supabase
        .from('conversions')
        .insert({
          user_id: user.id,
          video_url: youtubeUrl,
          video_id: videoId,
          options_selected: generateOptions,
          category_id: details.categoryId,
          category_name: categoryName
        });

      if (conversionError) {
        console.error('Error recording conversion:', conversionError);
      }
      
      toast.loading('Fetching video details...', { id: 'video-fetch' });
      
      const transcript = await YouTubeService.getTranscript(videoId);
      
      toast.success('Video details fetched successfully!', { id: 'video-fetch' });
      
      setVideoDetails({
        id: videoId,
        title: details.title,
        thumbnail: details.thumbnail
      });

      toast.loading('Generating study materials...', { id: 'content-gen' });

      // Generate all content in parallel
      const [bookData, glossaryData, flashcardsData] = await Promise.allSettled([
        YouTubeService.generateBook(transcript, videoId, details.title, {
          channelTitle: details.channelTitle,
          publishedAt: details.publishedAt,
          viewCount: details.viewCount,
          likeCount: details.likeCount,
          duration: YouTubeService.formatDuration(details.duration),
        }),
        generateOptions.generateGlossary ? YouTubeService.generateGlossary(transcript, videoId, details.title) : Promise.resolve(null),
        generateOptions.generateFlashcards ? YouTubeService.generateFlashcards(transcript, videoId, details.title) : Promise.resolve(null)
      ]);

      console.log('Generation results:', {
        bookData: bookData.status,
        glossaryData: glossaryData.status,
        flashcardsData: flashcardsData.status
      });

      if (bookData.status === 'rejected') {
        console.error('Book generation failed:', bookData.reason);
        throw new Error('Failed to generate study materials');
      }

      if (glossaryData.status === 'rejected') {
        console.error('Glossary generation failed:', glossaryData.reason);
        toast.error('Failed to generate glossary');
      }

      if (flashcardsData.status === 'rejected') {
        console.error('Flashcards generation failed:', flashcardsData.reason);
        toast.error('Failed to generate flashcards');
      }

      // Generate MCQs only if book data is available
      const mcqData = generateOptions.generateMCQ ? 
        await Promise.allSettled([
          YouTubeService.generateMCQ(bookData.value.chapters)
        ]).then(results => {
          if (results[0].status === 'rejected') {
            console.error('MCQ generation failed:', results[0].reason);
            toast.error('Failed to generate MCQs');
            return { status: 'fulfilled', value: null };
          }
          return results[0];
        }) :
        { status: 'fulfilled', value: null };

      console.log('MCQ generation result:', mcqData);

      // Process and structure the content
      const processedNotes = bookData.value ? {
        summary: bookData.value.summary || bookData.value.chapters[0]?.content.slice(0, 200) || '',
        readingTime: YouTubeService.formatDuration(details.duration),
        keyTopics: bookData.value.chapters?.map((ch: Chapter) => ch.title).slice(0, 3) || [],
        chapters: bookData.value.chapters?.map((chapter: Chapter, index: number) => ({
          title: chapter.title,
          content: chapter.content,
          isActive: index === 0,
          concepts: chapter.concepts || [],
          takeaways: Array.isArray(chapter.takeaways) ? chapter.takeaways : [chapter.takeaway || '']
        })) || []
      } : null;

      const glossaryContent = glossaryData.status === 'fulfilled' && glossaryData.value ? glossaryData.value : null;
      const flashcardsContent = flashcardsData.status === 'fulfilled' && flashcardsData.value ? flashcardsData.value : null;
      const mcqContent = mcqData.status === 'fulfilled' && mcqData.value ? mcqData.value : null;

      // Generate mindmap if enabled
      const mindmapData = generateOptions.generateMindmap ?
        await Promise.allSettled([
          YouTubeService.generateMindmap(transcript, videoId, details.title)
        ]).then(results => {
          if (results[0].status === 'rejected') {
            console.error('Mindmap generation failed:', results[0].reason);
            toast.error('Failed to generate mindmap');
            return { status: 'fulfilled', value: null };
          }
          return results[0];
        }) :
        { status: 'fulfilled', value: null };

      if (!processedNotes) {
        throw new Error('Failed to generate study materials');
      }

      setGeneratedContent({
        notes: processedNotes,
        glossary: glossaryContent,
        mcq: mcqContent,
        flashcards: flashcardsContent,
        mindmap: mindmapData.status === 'fulfilled' ? mindmapData.value : null
      });

      setShowContent(true);
      toast.success('Study materials generated successfully!', { id: 'content-gen' });
    } catch (error) {
      console.error('Content generation error:', error);
      toast.error('Failed to generate study materials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    setSelectedAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[questionIndex] = answerIndex;
      return newAnswers;
    });
  };

  const handleCheckboxChange = (option: keyof GenerateOptions) => (checked: boolean) => {
    setGenerateOptions(prev => ({ ...prev, [option]: checked }));
  };

  const handlePdfDownload = async () => {
    if (!generatedContent.notes) return;
    
    setIsDownloading(true);
    try {
      await PDFService.generateStudyGuide({
        title: videoDetails?.title || 'Study Guide',
        chapters: generatedContent.notes.chapters.map(chapter => ({
          ...chapter,
          takeaways: Array.isArray(chapter.takeaways) ? chapter.takeaways : [chapter.takeaway || '']
        }))
      });
      toast.success('Study guide downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download study guide');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleChapterSelect = (selectedIndex: number) => {
    setGeneratedContent(prev => ({
      ...prev,
      notes: prev.notes ? {
        ...prev.notes,
        chapters: prev.notes.chapters.map((chapter, index) => ({
          ...chapter,
          isActive: index === selectedIndex
        }))
      } : null
    }));
  };

  return (
    <div className="min-h-screen">
    <div className="container mx-auto px-4 py-8">
      {/* Hero Section */}
      <div className="text-center px-4 md:px-8 mb-8 md:mb-12">
          <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 ${
            theme === 'reading'
              ? 'text-gray-800'
              : 'text-gray-900 dark:text-gray-100'
          }`}>
          Transform YouTube Videos into Study Materials
        </h1>
          <p className={`text-base md:text-lg lg:text-xl ${
            theme === 'reading'
              ? 'text-gray-700'
              : 'text-gray-600 dark:text-gray-300'
          }`}>
          Enter a YouTube URL to generate comprehensive study materials
        </p>
      </div>

      {/* URL Input and Options Section */}
      <div className="max-w-2xl mx-auto px-4 md:px-8 mb-8 md:mb-12">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 md:w-6 md:h-6 ${
                theme === 'reading'
                  ? 'text-gray-500'
                  : 'text-gray-400 dark:text-gray-500'
              }`} />
            <input
              type="text"
              placeholder="Paste YouTube URL here"
                className={`w-full pl-10 md:pl-12 pr-4 py-3 md:py-4 rounded-lg border text-base md:text-lg ${
                  theme === 'reading'
                    ? 'bg-[#FAF9F6] border-gray-200 text-gray-800 focus:ring-amber-500'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-[#2D7FFF] dark:focus:ring-[#1A3C75]'
                }`}
              value={youtubeUrl}
              onChange={(e) => {
                setYoutubeUrl(e.target.value);
                validateYoutubeUrl(e.target.value);
              }}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!isValidUrl || isLoading}
              className={`w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 text-base md:text-lg h-auto ${
                theme === 'reading'
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-[#0056D2] dark:bg-[#2D7FFF] hover:bg-[#004BB8] dark:hover:bg-[#1A3C75] text-white'
              }`}
          >
            {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Generate'
            )}
          </Button>
        </div>

        {/* Generation Options */}
          <div className={`overflow-x-auto -mx-4 md:mx-0 ${
            theme === 'reading'
              ? 'bg-[#FAF9F6]'
              : 'bg-gray-50 dark:bg-gray-800/50'
          }`}>
            <div className="flex items-center justify-start md:justify-center px-4 py-3 min-w-max md:min-w-0 gap-6 md:gap-8">
              <label className="flex items-center gap-2 cursor-pointer group whitespace-nowrap">
                <Checkbox
                  checked={generateOptions.generateNotes}
                  onCheckedChange={handleCheckboxChange('generateNotes')}
                  className="w-4 h-4 border-gray-300 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-[#0056D2] dark:group-hover:text-[#2D7FFF] transition-colors">Smart Notes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group whitespace-nowrap">
                <Checkbox
                  checked={generateOptions.generateMCQ}
                  onCheckedChange={handleCheckboxChange('generateMCQ')}
                  className="w-4 h-4 border-gray-300 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-[#0056D2] dark:group-hover:text-[#2D7FFF] transition-colors">Practice MCQs</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group whitespace-nowrap">
                <Checkbox
                  checked={generateOptions.generateGlossary}
                  onCheckedChange={handleCheckboxChange('generateGlossary')}
                  className="w-4 h-4 border-gray-300 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-[#0056D2] dark:group-hover:text-[#2D7FFF] transition-colors">Glossary</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group whitespace-nowrap">
                <Checkbox
                  checked={generateOptions.generateFlashcards}
                  onCheckedChange={handleCheckboxChange('generateFlashcards')}
                  className="w-4 h-4 border-gray-300 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-[#0056D2] dark:group-hover:text-[#2D7FFF] transition-colors">Flashcards</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group whitespace-nowrap">
                <Checkbox
                  checked={generateOptions.generateMindmap}
                  onCheckedChange={handleCheckboxChange('generateMindmap')}
                  className="w-4 h-4 border-gray-300 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-[#0056D2] dark:group-hover:text-[#2D7FFF] transition-colors">Concept Map</span>
              </label>
            </div>
          </div>
      </div>

        {/* Split View Content */}
        {showContent && videoDetails && (
          <div className="space-y-8">
            {/* Video and Quick Summary Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left - Video Player */}
              <div className="space-y-6">
                <div className="bg-gray-900 rounded-xl overflow-hidden">
          <VideoPlayer
            videoId={videoDetails.id}
                    title={videoDetails.title}
            thumbnail={videoDetails.thumbnail}
          />
        </div>
                {/* Main Topics under video */}
                <div className={`p-6 rounded-xl ${
                  theme === 'reading'
                    ? 'bg-[#FAF9F6]'
                    : 'bg-white dark:bg-gray-800'
                }`}>
                  <h3 className={`text-xl font-bold mb-4 ${
                    theme === 'reading'
                      ? 'text-gray-800'
                      : 'text-gray-800 dark:text-gray-100'
                  }`}>MAIN TOPICS</h3>
                  <div className="flex flex-wrap gap-2">
                    {generatedContent.notes?.keyTopics.map((topic, index) => (
                      <span 
                        key={index}
                        className={`px-4 py-2 rounded-full text-base font-medium ${
                          index === 0 
                            ? 'bg-[#0056D2] dark:bg-[#2D7FFF] text-white' // Primary topic
                            : 'bg-[#E8F1FF] dark:bg-[#1A3C75] text-[#0056D2] dark:text-[#60A5FA]' // Secondary topics
                        }`}
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                  </div>
                </div>
                
              {/* Right - Quick Summary */}
              <div className={`p-8 rounded-xl ${
                theme === 'reading'
                  ? 'bg-[#FAF9F6]'
                  : 'bg-white dark:bg-gray-800'
              }`}>
                <div className="space-y-6">
                  <h2 className={`text-3xl font-bold ${
                    theme === 'reading'
                      ? 'text-gray-800'
                      : 'text-gray-800 dark:text-gray-100'
                  }`}>Quick Summary</h2>
                  <p className={`text-lg ${
                    theme === 'reading'
                      ? 'text-gray-700'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {generatedContent.notes?.summary}
                  </p>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Clock className={`w-5 h-5 ${
                        theme === 'reading'
                          ? 'text-amber-600'
                          : 'text-[#0056D2] dark:text-[#60A5FA]'
                      }`} />
                      <span className={`text-base ${
                        theme === 'reading'
                          ? 'text-gray-600'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {generatedContent.notes?.readingTime}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className={`w-5 h-5 ${
                        theme === 'reading'
                          ? 'text-amber-600'
                          : 'text-[#0056D2] dark:text-[#60A5FA]'
                      }`} />
                      <span className={`text-base ${
                        theme === 'reading'
                          ? 'text-gray-600'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {generatedContent.notes?.chapters?.length || 0} chapters
                            </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Tabs */}
            <div className={`rounded-xl shadow-lg ${
              theme === 'reading'
                ? 'bg-[#FAF9F6]'
                : 'bg-white dark:bg-gray-800 dark:shadow-gray-900/30'
            }`}>
              {/* Tab Navigation */}
              <div className="border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between px-8">
                  <nav className="flex overflow-x-auto">
                    {[
                      { id: 'notes', label: 'Smart Notes', icon: BookOpen },
                      { id: 'mcq', label: 'Practice MCQs', icon: ListChecks },
                      { id: 'flashcards', label: 'Flashcards', icon: Library },
                      { id: 'glossary', label: 'Glossary', icon: BookMarked },
                      { id: 'mindmap', label: 'Concept Map', icon: Network }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as TabType)}
                        className={`flex items-center gap-3 min-w-max px-8 py-6 text-xl font-medium border-b-2 transition-colors ${
                          activeTab === tab.id
                            ? 'border-[#2D7FFF] text-[#2D7FFF] dark:text-[#60A5FA] bg-[#E8F1FF] dark:bg-[#1A3C75]'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <tab.icon className="w-6 h-6" />
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                  <Button
                    onClick={handlePdfDownload}
                    disabled={isDownloading || activeTab === 'flashcards' || activeTab === 'mindmap'}
                    variant="outline"
                    size="sm"
                    className="gap-2 border-[#0056D2] dark:border-[#2D7FFF] text-[#0056D2] dark:text-[#60A5FA] hover:bg-[#E8F1FF] dark:hover:bg-[#1A3C75] text-lg py-3 h-auto px-6"
                  >
                    {isDownloading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Download className="h-5 w-5" />
                    )}
                    Download {activeTab === 'mcq' ? 'Quiz' : activeTab}
                  </Button>
                  </div>
                </div>
                
              {/* Tab Content */}
              <div className="p-8">
                <div id="content-for-pdf">
                  {/* Smart Notes Content */}
                  {activeTab === 'notes' && (
                    <div className="space-y-10">
                      {/* Chapter Navigation */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(generatedContent.notes?.chapters || []).map((chapter, index) => (
                          <div 
                            key={index}
                            onClick={() => handleChapterSelect(index)}
                            className={`p-6 rounded-xl border-2 transition-all cursor-pointer ${
                              theme === 'reading'
                                ? chapter.isActive
                                  ? 'border-amber-500 bg-amber-50'
                                  : 'border-gray-200 hover:border-amber-200 hover:bg-amber-50/30'
                                : chapter.isActive 
                                  ? 'border-[#2D7FFF] dark:border-[#60A5FA] bg-[#E8F1FF] dark:bg-[#1A3C75]' 
                                  : 'border-gray-100 dark:border-gray-700 hover:border-[#E8F1FF] dark:hover:border-[#1A3C75] hover:bg-[#E8F1FF]/30 dark:hover:bg-[#1A3C75]/30'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                                theme === 'reading'
                                  ? chapter.isActive
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-gray-100 text-gray-500'
                                  : chapter.isActive 
                                    ? 'bg-[#0056D2] dark:bg-[#2D7FFF] text-white' 
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}>
                                {index + 1}
                              </div>
                              <h3 className={`font-medium text-xl ${
                                theme === 'reading'
                                  ? 'text-amber-800'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                {chapter.title}
                              </h3>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Chapter Content */}
                      <div className="space-y-8">
                        {/* Key Concepts Section */}
                        <div className="mb-10">
                          <h3 className={`text-3xl font-bold mb-6 ${
                            theme === 'reading'
                              ? 'text-gray-800'
                              : 'text-gray-800 dark:text-gray-100'
                          }`}>Key Concepts</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {(generatedContent.notes?.chapters?.find(ch => ch.isActive)?.concepts || []).map((concept, index) => (
                              <div key={index} className={`p-6 rounded-xl border-2 ${
                                theme === 'reading'
                                  ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-[#FAF9F6] to-amber-50/30'
                                  : 'border-[#0056D2]/20 dark:border-[#2D7FFF]/20 bg-gradient-to-br from-[#E8F1FF] via-white to-[#E8F1FF]/30 dark:from-[#1A3C75] dark:via-gray-800 dark:to-[#1A3C75]/30'
                              }`}>
                                <h4 className={`font-semibold text-xl mb-3 ${
                                  theme === 'reading'
                                    ? 'text-amber-800'
                                    : 'text-[#0056D2] dark:text-[#60A5FA]'
                                }`}>{concept.title}</h4>
                                <p className={`text-lg leading-relaxed ${
                                  theme === 'reading'
                                    ? 'text-gray-700'
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>{concept.description}</p>
                    </div>
                  ))}
                </div>
              </div>
                        
                        {/* Detailed Notes */}
                        <div className="mb-10">
                          <h3 className={`text-3xl font-bold mb-6 ${
                            theme === 'reading'
                              ? 'text-gray-800'
                              : 'text-gray-800 dark:text-gray-100'
                          }`}>Detailed Notes</h3>
                          <p className={`text-lg ${
                            theme === 'reading'
                              ? 'text-gray-700 leading-relaxed'
                              : 'text-gray-700 dark:text-gray-300 leading-relaxed'
                          }`}>
                            {generatedContent.notes?.chapters?.find(ch => ch.isActive)?.content || 'Content is being generated...'}
                          </p>
                </div>
                
                        {/* Chapter Takeaways */}
                        <div className={`mb-10 p-6 rounded-xl border-2 ${
                          theme === 'reading'
                            ? 'border-amber-200 bg-gradient-to-r from-amber-50 via-[#FAF9F6] to-amber-50/30'
                            : 'border-[#0056D2]/20 dark:border-[#2D7FFF]/20 bg-gradient-to-r from-[#E8F1FF] via-white to-[#E8F1FF]/30 dark:from-[#1A3C75] dark:via-gray-800 dark:to-[#1A3C75]/30'
                        }`}>
                          <h3 className={`text-3xl font-bold mb-4 ${
                            theme === 'reading'
                              ? 'text-gray-800'
                              : 'text-gray-800 dark:text-gray-100'
                          }`}>Chapter Takeaways</h3>
                          <ul className="space-y-4">
                            {(generatedContent.notes?.chapters?.find(ch => ch.isActive)?.takeaways || []).map((takeaway, index) => (
                              <li key={index} className="flex items-start gap-3">
                                <div className={`flex-shrink-0 w-2.5 h-2.5 mt-3 rounded-full ${
                                  theme === 'reading'
                                    ? 'bg-amber-500'
                                    : 'bg-[#0056D2] dark:bg-[#2D7FFF]'
                                }`}></div>
                                <p className={`text-lg ${
                                  theme === 'reading'
                                    ? 'text-gray-700 leading-relaxed'
                                    : 'text-gray-700 dark:text-gray-300 leading-relaxed'
                                }`}>{takeaway}</p>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Related Terms */}
                        {generatedContent.glossary?.terms && generatedContent.glossary.terms.length > 0 && (
                          <div className="my-6">
                            <h3 className={`text-3xl font-bold mb-4 ${
                              theme === 'reading'
                                ? 'text-gray-800'
                                : 'text-gray-800 dark:text-gray-100'
                            }`}>Related Terms</h3>
                            <div className="flex flex-wrap gap-2">
                              {generatedContent.glossary.terms.map((term, index) => (
                                <div key={index} className="group relative">
                                  <span className="px-3 py-1 bg-[#E8F1FF] dark:bg-[#1A3C75] text-[#0056D2] dark:text-[#60A5FA] rounded-full text-sm cursor-help">
                                    {term.term}
                                  </span>
                                  <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900/30 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">{term.definition}</p>
                                  </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

                        {/* Quick Check */}
                        {generatedContent.mcq?.questions?.[0] && 
                          Array.isArray(generatedContent.mcq.questions[0].options) && 
                          generatedContent.mcq.questions[0].options.length > 0 && (
                          <div className="hidden my-6 p-6 bg-gradient-to-br from-[#E8F1FF] via-white to-[#E8F1FF]/30 dark:from-[#1A3C75] dark:via-gray-800 dark:to-[#1A3C75]/30 rounded-xl">
                            {console.log('Quick Check MCQ data:', generatedContent.mcq.questions[0])}
                            <h3 className={`text-3xl font-bold mb-4 ${
                              theme === 'reading'
                                ? 'text-gray-800'
                                : 'text-gray-800 dark:text-gray-100'
                            }`}>Quick Check</h3>
                            <div className="space-y-3">
                              <p className="text-lg font-medium text-gray-800 dark:text-gray-200">
                                {generatedContent.mcq.questions[0].question}
                              </p>
                              <div className="space-y-2">
                                {generatedContent.mcq.questions[0].options.map((option, index) => (
                                  <button
                                    key={index}
                                    onClick={() => handleAnswerSelect(0, index)}
                                    className={`w-full p-5 text-left rounded-xl border-2 transition-all text-lg ${
                                      selectedAnswers[0] === index
                                        ? 'border-[#0056D2] dark:border-[#2D7FFF] bg-[#E8F1FF] dark:bg-[#1A3C75] text-[#0056D2] dark:text-[#60A5FA]'
                                        : 'border-gray-100 dark:border-gray-700 hover:border-[#0056D2]/30 dark:hover:border-[#2D7FFF]/30 hover:bg-[#E8F1FF]/50 dark:hover:bg-[#1A3C75]/50 text-gray-700 dark:text-gray-300'
                                    }`}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                  </div>
                </div>
                  )}

                  {/* MCQ Content */}
                  {activeTab === 'mcq' && (
                    <div className="max-w-3xl mx-auto space-y-10">
                      {console.log('Practice MCQs data:', generatedContent.mcq)}
                      <div className="flex items-center justify-between">
                        <h3 className={`text-3xl font-bold mb-6 ${
                          theme === 'reading'
                            ? 'text-gray-800'
                            : 'text-gray-800 dark:text-gray-100'
                        }`}>Practice Questions</h3>
                        <div className="flex items-center gap-4">
                          <span className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                            {generatedContent.mcq?.questions?.length || 0} questions
                          </span>
                          <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
                          <span className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                            {selectedAnswers.filter(a => a !== undefined).length} answered
                          </span>
                          <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
                          <span className="text-lg text-green-500 dark:text-green-400 font-medium">
                            {selectedAnswers.reduce((count, answer, index) => 
                              count + (answer === generatedContent.mcq?.questions?.[index]?.correctAnswer ? 1 : 0), 0)} correct
                          </span>
                        </div>
                      </div>
                      {generatedContent.mcq?.questions?.length ? (
                        generatedContent.mcq.questions.map((q, qIndex) => (
                          <div key={qIndex} className="space-y-5 pb-8 border-b-2 border-gray-100 dark:border-gray-700 last:border-0">
                            <p className="text-2xl font-medium text-gray-800 dark:text-gray-200">{qIndex + 1}. {q.question}</p>
                            <div className="space-y-3 pl-6">
                              {q.options.map((option, oIndex) => {
                                const isSelected = selectedAnswers[qIndex] === oIndex;
                                const isCorrect = q.correctAnswer === oIndex;
                                const showResult = selectedAnswers[qIndex] !== undefined;
                                
                                return (
                                  <button
                                    key={oIndex}
                                    onClick={() => handleAnswerSelect(qIndex, oIndex)}
                                    disabled={showResult}
                                    className={`w-full p-5 text-left rounded-xl border-2 transition-all text-lg ${
                                      showResult
                                        ? isCorrect
                                          ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                          : isSelected
                                            ? 'border-red-500 dark:border-red-600 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                                            : 'border-gray-100 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                                        : isSelected
                                          ? 'border-[#0056D2] dark:border-[#2D7FFF] bg-[#E8F1FF] dark:bg-[#1A3C75] text-[#0056D2] dark:text-[#60A5FA]'
                                          : 'border-gray-100 dark:border-gray-700 hover:border-[#0056D2]/30 dark:hover:border-[#2D7FFF]/30 hover:bg-[#E8F1FF]/50 dark:hover:bg-[#1A3C75]/50 text-gray-700 dark:text-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span>{option}</span>
                                      {showResult && (isCorrect || isSelected) && (
                                        <span className={`ml-2 ${isCorrect ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                          {isCorrect ? '✓' : '✗'}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                            {selectedAnswers[qIndex] !== undefined && (
                              <div className={`mt-4 p-4 rounded-lg ${
                                selectedAnswers[qIndex] === q.correctAnswer
                                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                              }`}>
                                <p className={`font-medium mb-2 ${
                                  selectedAnswers[qIndex] === q.correctAnswer
                                    ? 'text-green-700 dark:text-green-400'
                                    : 'text-red-700 dark:text-red-400'
                                }`}>
                                  {selectedAnswers[qIndex] === q.correctAnswer ? 'Correct!' : 'Incorrect'}
                                </p>
                                <p className="text-gray-700 dark:text-gray-300">
                                  {q.explanation || `The correct answer is option ${String.fromCharCode(65 + q.correctAnswer)}.`}
                                </p>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No practice questions available yet.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Flashcards Content */}
                  {activeTab === 'flashcards' && (
                    <div className="max-w-2xl mx-auto space-y-8">
                      {console.log('Rendering flashcards tab, content:', generatedContent.flashcards)}
                      <div className="flex items-center justify-between">
                        <h3 className={`text-3xl font-bold mb-6 ${
                          theme === 'reading'
                            ? 'text-gray-800'
                            : 'text-gray-800 dark:text-gray-100'
                        }`}>Flashcards</h3>
                        <span className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                          {generatedContent.flashcards?.totalCards || 0} cards
                        </span>
                      </div>
                      
                      {generatedContent.flashcards ? (
                        <FlashcardDisplay data={generatedContent.flashcards} />
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No flashcards available yet.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Glossary Content */}
                  {activeTab === 'glossary' && (
                    <div className="max-w-3xl mx-auto space-y-8">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-3xl font-bold mb-6 ${
                          theme === 'reading'
                            ? 'text-gray-800'
                            : 'text-gray-800 dark:text-gray-100'
                        }`}>Glossary</h3>
                        <span className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                          {generatedContent.glossary?.terms?.length || 0} terms
                        </span>
                      </div>
                      {generatedContent.glossary?.terms?.length ? (
                        <div className="grid gap-4">
                          {generatedContent.glossary.terms.map((term, index) => (
                            <div key={index} className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-[#E8F1FF]/30 dark:hover:bg-[#1A3C75]/30 transition-colors">
                              <h4 className={`font-medium mb-2 ${
                                theme === 'reading'
                                  ? 'text-amber-800'
                                  : 'text-[#0056D2] dark:text-[#60A5FA]'
                              }`}>{term.term}</h4>
                              <p className={`${
                                theme === 'reading'
                                  ? 'text-gray-700 tracking-wide'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>{term.definition}</p>
                    </div>
                  ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No glossary terms available yet.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mindmap Content */}
                  {activeTab === 'mindmap' && (
                    <div className="max-w-4xl mx-auto space-y-8">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-3xl font-bold mb-6 ${
                          theme === 'reading'
                            ? 'text-gray-800'
                            : 'text-gray-800 dark:text-gray-100'
                        }`}>Concept Map</h3>
                      </div>
                      {generatedContent.mindmap ? (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                          {console.log('Rendering mindmap with data:', generatedContent.mindmap)}
                          <MindmapView markdown={generatedContent.mindmap} />
                        </div>
                      ) : (
                        <div className="aspect-[3/2] bg-gray-50 dark:bg-gray-800/50 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-700">
                          <p className="text-gray-500 dark:text-gray-400">No mindmap data available yet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
    </div>
  );
} 